import React, { useEffect, useMemo, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'

// 부서별 인원. 두 군데에 쓰인다.
//
//   배부      본부장·대표님 카드처럼 한 팀에 넣을 수 없는 지출은 공통 부서에 담아 두었다가
//             부서별 손익을 뽑을 때 인원수에 비례해 나눈다.
//   인당생산성  분모가 인원이다.
//
// 월별로 남긴다. 지금 인원 하나만 들고 있으면 사람이 들고 나는 순간 과거 수치까지 흔들린다.
// 8월 인당생산성을 9월에 다시 뽑아도 같은 값이 나와야 한다.
//
// 매달 스무 칸을 새로 치게 하면 아무도 안 채우므로 지난달을 가져오는 단추를 둔다.

const 이번달 = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
const 지난달 = (ym) => {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 2, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const 범위말 = { company: '전 부서로 배부', division: '같은 본부로 배부', bu: '같은 사업부로 배부' }

export default function Headcount() {
  const [ym, setYm] = useState(이번달())
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [drafts, setDrafts] = useState({})
  const [toast, setToast] = useState('')

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2600) }

  const load = async (달 = ym) => {
    setErr('')
    try {
      setData(await portal.get(`/api/meta/headcount?ym=${encodeURIComponent(달)}`))
      setDrafts({})
    } catch (e) { setErr(e.message) }
  }
  useEffect(() => { load(ym) }, [ym])

  const 합 = useMemo(() => {
    if (!data) return { 인원: 0, 채운부서: 0, 공통: 0 }
    let 인원 = 0, 채운부서 = 0, 공통 = 0
    for (const r of data.rows) {
      인원 += r.headcount || 0
      if (r.headcount > 0) 채운부서 += 1
      if (r.allocScope) 공통 += 1
    }
    return { 인원, 채운부서, 공통 }
  }, [data])

  const save = async (code, v) => {
    try {
      await portal.post('/api/meta/headcount', { ym, deptCode: code, headcount: v })
      setDrafts((d) => { const n = { ...d }; delete n[code]; return n })
      load()
    } catch (e) { setErr(e.message) }
  }

  const 지난달가져오기 = async () => {
    const 전 = 지난달(ym)
    try {
      const r = await portal.post('/api/meta/headcount', { ym, copyFrom: 전 })
      notify(r.copied ? `${전} 인원 ${r.copied}개 부서를 가져왔습니다` : `${전} 에 넣어 둔 인원이 없습니다`)
      load()
    } catch (e) { setErr(e.message) }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        <span className="muted">기준월</span>
        <input type="month" value={ym} onChange={(e) => e.target.value && setYm(e.target.value)} />
        <button className="btn secondary" onClick={지난달가져오기}>지난달 인원 가져오기</button>
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>
          {ym} 합계 {fmt(합.인원, 1)}명 · 넣은 부서 {합.채운부서}
          {합.공통 ? ` · 공통 ${합.공통}` : ''}
        </span>
      </div>

      <div className="hint" style={{ marginBottom: 10 }}>
        공통비 배부와 인당생산성이 이 인원을 씁니다. <b>월별로 남기므로</b> 사람이 들고 나도 과거 수치는 흔들리지 않습니다.
        <br />
        회색으로 표시된 <b>공통 부서</b>에는 인원을 넣지 않습니다 — 거기 담긴 지출을 다른 부서로 나눠 주는 자리입니다.
        겸직이나 파트타임은 0.5 처럼 소수로 적으셔도 됩니다.
      </div>

      {err && <div className="error-box">{err}</div>}
      {!err && !data && <div className="muted">불러오는 중…</div>}

      {data && (
        <div className="panel">
          <table>
            <thead>
              <tr>
                <th className="l" style={{ width: 120 }}>본부</th>
                <th className="l" style={{ width: 170 }}>부서</th>
                <th className="l" style={{ width: 110 }}>사업부</th>
                <th style={{ width: 110 }}>인원</th>
                <th className="l">비고</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const 공통 = !!r.allocScope
                const v = drafts[r.code] !== undefined ? drafts[r.code] : String(r.headcount ?? 0)
                return (
                  <tr key={r.code} style={공통 ? { background: 'rgba(148,163,184,.10)' } : undefined}>
                    <td className="l muted" style={{ fontSize: 12 }}>{r.division || '—'}</td>
                    <td className="l">
                      {r.name}{' '}
                      <span className="muted" style={{ fontSize: 11 }}>{r.code}</span>
                    </td>
                    <td className="l muted" style={{ fontSize: 12 }}>{r.buName}</td>
                    <td className="c">
                      {공통 ? (
                        <span className="muted" style={{ fontSize: 11 }}>—</span>
                      ) : (
                        <input value={v} inputMode="decimal" style={{ width: 70, textAlign: 'right' }}
                          onChange={(e) => setDrafts((d) => ({ ...d, [r.code]: e.target.value }))}
                          onBlur={() => {
                            const n = Number(v)
                            if (!Number.isNaN(n) && n !== r.headcount) save(r.code, n)
                            else setDrafts((d) => { const x = { ...d }; delete x[r.code]; return x })
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }} />
                      )}
                    </td>
                    <td className="l muted" style={{ fontSize: 12 }}>
                      {공통 ? (범위말[r.allocScope] || r.allocScope) : (r.memo || '')}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
